import { Box, type PopoverOrigin } from '@mui/material';
import { DotsThreeIcon } from '@phosphor-icons/react/dist/ssr/DotsThree';
import { vars } from '@tgtg/kit';
import { useState, type MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import msg from '../messages';
import NavPopover from '../NavPopover';

import { isItemActive, toTarget } from './navItemPath';

import type { NavigationBarItem } from '@tgtg/kit';

const ANCHOR_ORIGIN: PopoverOrigin = { vertical: 'top', horizontal: 'center' };
const TRANSFORM_ORIGIN: PopoverOrigin = { vertical: 'bottom', horizontal: 'center' };

// Mirrors the kit's bottom-variant item styling; its own class names are build hashes.
const triggerStyle = {
  flex: 1,
  maxWidth: vars.size.size900,
  height: vars.size.size600,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: vars.spacing.neutral.small,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  outline: 'none',
  '&:focus-visible': {
    outline: `${vars.border.neutral.medium} solid ${vars.color.border.interactive.focusDefault}`,
    outlineOffset: vars.spacing.neutral.xxsmall,
    borderRadius: vars.radius.full,
  },
};

const iconStyle = { width: vars.size.size300, height: vars.size.size300 };

const labelStyle = {
  fontFamily: vars.typography.labels.xSmall.fontFamily,
  fontWeight: vars.typography.labels.xSmall.fontWeight,
  fontSize: vars.typography.labels.xSmall.fontSize,
  lineHeight: vars.typography.labels.xSmall.lineHeight,
  letterSpacing: vars.typography.labels.xSmall.letterSpacing,
  color: 'currentColor',
  textAlign: 'center',
};

type BottomBarMorePopoverProps = {
  items: NavigationBarItem[];
  isActive?: boolean;
};

const BottomBarMorePopover = ({ items, isActive }: BottomBarMorePopoverProps) => {
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const popoverItems = items.map((item) => ({
    id: item.id,
    to: toTarget(item.to),
    label: item.label,
    icon: item.icon,
    isActive: isItemActive(item, pathname),
  }));

  return (
    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
      <Box
        component='button'
        type='button'
        onClick={handleOpen}
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup='true'
        aria-current={isActive ? 'page' : undefined}
        sx={{
          ...triggerStyle,
          color: isActive ? vars.color.content.brand : vars.color.content.subtlest,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DotsThreeIcon style={iconStyle} aria-hidden='true' />
        </Box>
        <Box component='span' sx={labelStyle}>
          <FormattedMessage {...msg.more} />
        </Box>
      </Box>
      <NavPopover
        items={popoverItems}
        anchorEl={anchorEl}
        isOpen={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
      />
    </Box>
  );
};

export default BottomBarMorePopover;
